import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { AoSGameState } from '../../types';
import { getIncomeReduction } from '../../types';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 8: 収入減少フェーズ
 *
 * 各プレイヤーの収入レベルを収入レベルに応じて自動的に減少させます。
 * ロジック処理後、ユーザーがクリックするまで待機します。
 *
 * 処理内容:
 * - 収入レベルに応じた減少幅を計算
 * - 減少幅は収入レベルが高いほど大きい
 * - 減少により収入レベルが0以下になった場合は破産
 * - 倒産プレイヤーは処理をスキップ
 *
 * 減少テーブル:
 * - 51以上: -10
 * - 41-50: -8
 * - 31-40: -6
 * - 21-30: -4
 * - 11-20: -2
 * - 0-10: 0（減少なし）
 *
 * 参照:
 * - docs/specifications/logic/common/phase_08_income_reduction.md
 * - docs/specifications/logic/barbados/phase_08_income_reduction_override.md
 */
export const incomeReductionPhase: PhaseConfig<AoSGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[incomeReductionPhase] ★★★ Phase 8: Income Reduction started ★★★');
    debug.log('[incomeReductionPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    // ソロプレイ用: Player 0の収入減少
    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      // 倒産時は即座に次フェーズへ（結果表示なし）
      G.autoPhaseResult = null;
      return;
    }

    // 減少幅を計算
    const reduction = getIncomeReduction(player.incomeLevel);

    // 収入レベルを減少
    const oldIncomeLevel = player.incomeLevel;
    player.incomeLevel -= reduction;

    debug.log(`[incomeReductionPhase] Player 0 incomeLevel: ${oldIncomeLevel} → ${player.incomeLevel} (reduction: ${reduction})`);

    // 収入レベルが0を下回った場合（負の場合）は破産
    if (player.incomeLevel < 0) {
      player.isBankrupt = true;
      G.isGameOver = true;
      G.endReason = 'BANKRUPTCY';
      debug.log('[incomeReductionPhase] ★ Player BANKRUPT - Income dropped below 0');

      // 破産の処理結果を保存
      G.autoPhaseResult = {
        phase: 'incomeReduction',
        message: 'BANKRUPT! Income dropped to 0.',
        floatingText: `BANKRUPT!`,
        details: { reduction, bankrupt: true },
      };
    } else {
      // 処理結果を保存（UI表示用）
      G.autoPhaseResult = {
        phase: 'incomeReduction',
        message: 'Reducing income based on current level...',
        floatingText: reduction > 0 ? `-${reduction} Income` : 'No reduction',
        details: { reduction },
      };
    }

    debug.log('[incomeReductionPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: AoSGameState; events: any }) => {
      // Phase内でのみ呼び出し可能（Boardgame.ioが保証）
      G.autoPhaseResult = null;
      events.endPhase();
    },
    undoToTurnStart,
  },

  // 倒産プレイヤーは自動的に次フェーズへ、または Undo 強制遷移
  endIf: ({ G }: { G: AoSGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[incomeReductionPhase] Force transition to issueShares (Undo)');
      return true;
    }

    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      return true;
    }
    return false;
  },

  onEnd: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[incomeReductionPhase] ★★★ onEnd called - Phase 8 ended ★★★');
    debug.log('[incomeReductionPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });
    debug.log('[incomeReductionPhase] ★★★ Next phase should be: refillGoods');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('incomeReduction');
  },

  // Phase 9 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: AoSGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'refillGoods';
  }
};
